/**
 * AI Model SubPurpose Schema v2
 *
 * Defines hierarchical sub-purposes for AI models based on nodeType + purpose combination.
 * SubPurposes are specific capabilities within a purpose category.
 *
 * Hierarchy: NodeType → Purpose → SubPurpose
 *
 * Examples:
 * - image → enhance → upscale
 * - video → create → text-to-video
 * - sound → create → text-to-speech
 * - 3d → edit → remesh
 *
 * @module @genieartinc/schemas/pools/ai
 */

import { z } from 'zod';
import type { AiNodeType, AiModelPurpose } from './model.schema';

// =============================================================================
// SUB-PURPOSE DEFINITIONS
// =============================================================================

/**
 * Text LLM Node SubPurposes
 */
export const TEXT_LLM_SUB_PURPOSES = {
  create: [],
  edit: [],
  enhance: [],
  tools: [],
} as const;

/**
 * Image Node SubPurposes
 */
export const IMAGE_SUB_PURPOSES = {
  create: [],
  edit: [],
  enhance: ['upscale', 'denoise', 'restore', 'super-resolution', 'sharpen', 'hdr'],
  tools: [
    'inpaint', 'outpaint', 'controlnet', 'mask', 'background-removal', 'style-transfer',
    'reimagine', 'object-removal', 'depth-estimation', 'pose-estimation',
    'edge-detection', 'segmentation', 'captioning', 'face-swap', 'face-retouching',
    'virtual-try-on', 'relighting', 'colorization', 'vectorization',
    'product-photography', 'training',
  ],
} as const;

/**
 * Video Node SubPurposes
 */
export const VIDEO_SUB_PURPOSES = {
  create: ['text-to-video', 'image-to-video'],
  edit: [],
  enhance: ['video-upscale'],
  tools: [
    'lipsync', 'video-extend', 'frame-interpolation', 'motion-transfer',
    'frame-extraction', 'video-captioning', 'video-segmentation', 'ffmpeg', 'reframe', 'training',
  ],
} as const;

/**
 * Sound Node SubPurposes (renamed from AUDIO)
 */
export const SOUND_SUB_PURPOSES = {
  create: ['text-to-speech', 'text-to-music', 'text-to-sound'],
  edit: [],
  enhance: ['audio-enhance'],
  tools: ['transcription', 'voice-conversion', 'audio-analysis', 'waveform'],
} as const;

/**
 * 3D Node SubPurposes
 */
export const THREE_D_SUB_PURPOSES = {
  create: ['text-to-3d', 'image-to-3d'],
  edit: ['remesh', 'retexture', 'part-editing'],
  enhance: [],
  tools: [],
} as const;

// =============================================================================
// HIERARCHICAL MAPPING
// =============================================================================

/**
 * Complete NodeType → Purpose → SubPurpose mapping
 *
 * This is the single source of truth for valid subPurpose combinations.
 */
export const NODE_SUB_PURPOSE_MAP = {
  'text-llm': {
    create: TEXT_LLM_SUB_PURPOSES.create,
    edit: TEXT_LLM_SUB_PURPOSES.edit,
    enhance: TEXT_LLM_SUB_PURPOSES.enhance,
    tools: TEXT_LLM_SUB_PURPOSES.tools,
  },

  image: {
    create: IMAGE_SUB_PURPOSES.create,
    edit: IMAGE_SUB_PURPOSES.edit,
    enhance: IMAGE_SUB_PURPOSES.enhance,
    tools: IMAGE_SUB_PURPOSES.tools,
  },

  video: {
    create: VIDEO_SUB_PURPOSES.create,
    edit: VIDEO_SUB_PURPOSES.edit,
    enhance: VIDEO_SUB_PURPOSES.enhance,
    tools: VIDEO_SUB_PURPOSES.tools,
  },

  sound: {
    create: SOUND_SUB_PURPOSES.create,
    edit: SOUND_SUB_PURPOSES.edit,
    enhance: SOUND_SUB_PURPOSES.enhance,
    tools: SOUND_SUB_PURPOSES.tools,
  },

  '3d': {
    create: THREE_D_SUB_PURPOSES.create,
    edit: THREE_D_SUB_PURPOSES.edit,
    enhance: THREE_D_SUB_PURPOSES.enhance,
    tools: THREE_D_SUB_PURPOSES.tools,
  },
} as const;

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/**
 * Extract all possible subPurpose values from the mapping
 */
type ExtractSubPurposes<T> = T extends readonly (infer U)[] ? U : never;

type AllImageSubPurposes =
  | ExtractSubPurposes<typeof IMAGE_SUB_PURPOSES['enhance']>
  | ExtractSubPurposes<typeof IMAGE_SUB_PURPOSES['tools']>;

type AllVideoSubPurposes =
  | ExtractSubPurposes<typeof VIDEO_SUB_PURPOSES['create']>
  | ExtractSubPurposes<typeof VIDEO_SUB_PURPOSES['enhance']>
  | ExtractSubPurposes<typeof VIDEO_SUB_PURPOSES['tools']>;

type AllSoundSubPurposes =
  | ExtractSubPurposes<typeof SOUND_SUB_PURPOSES['create']>
  | ExtractSubPurposes<typeof SOUND_SUB_PURPOSES['enhance']>
  | ExtractSubPurposes<typeof SOUND_SUB_PURPOSES['tools']>;

type AllThreeDSubPurposes =
  | ExtractSubPurposes<typeof THREE_D_SUB_PURPOSES['create']>
  | ExtractSubPurposes<typeof THREE_D_SUB_PURPOSES['edit']>;

/**
 * Union type of all possible subPurpose values
 */
export type AiModelSubPurpose =
  | AllImageSubPurposes
  | AllVideoSubPurposes
  | AllSoundSubPurposes
  | AllThreeDSubPurposes;

// =============================================================================
// ZOD SCHEMA
// =============================================================================

/**
 * Zod schema for SubPurpose validation
 *
 * Note: This validates the string format but doesn't enforce hierarchical rules.
 * Use isValidSubPurpose() for full validation including nodeType + purpose.
 */
export const AiModelSubPurposeSchema = z.enum([
  // Image - Enhance
  'upscale',
  'denoise',
  'restore',
  'super-resolution',
  'sharpen',
  'hdr',

  // Image - Tools
  'inpaint',
  'outpaint',
  'controlnet',
  'mask',
  'background-removal',
  'style-transfer',
  'reimagine',
  'object-removal',
  'depth-estimation',
  'pose-estimation',
  'edge-detection',
  'segmentation',
  'captioning',
  'face-swap',
  'face-retouching',
  'virtual-try-on',
  'relighting',
  'colorization',
  'vectorization',
  'product-photography',
  'training',

  // Video - Create
  'text-to-video',
  'image-to-video',

  // Video - Enhance
  'video-upscale',

  // Video - Tools
  'lipsync',
  'video-extend',
  'frame-interpolation',
  'motion-transfer',
  'frame-extraction',
  'video-captioning',
  'video-segmentation',
  'ffmpeg',
  'reframe',
  // 'training' already listed under Image - Tools

  // Sound - Create
  'text-to-speech',
  'text-to-music',
  'text-to-sound',

  // Sound - Enhance
  'audio-enhance',

  // Sound - Tools
  'transcription',
  'voice-conversion',
  'audio-analysis',
  'waveform',

  // 3D - Create
  'text-to-3d',
  'image-to-3d',

  // 3D - Edit
  'remesh',
  'retexture',
  'part-editing',
]);

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get valid subPurposes for a specific nodeType + purpose combination
 *
 * @param nodeType - The node type (image, video, sound, 3d, text-llm)
 * @param purpose - The purpose (create, edit, enhance, tools)
 * @returns Array of valid subPurpose strings
 *
 * @example
 * ```typescript
 * getValidSubPurposes('image', 'enhance')
 * // Returns: ['upscale', 'denoise', 'restore', 'super-resolution', 'sharpen', 'hdr']
 *
 * getValidSubPurposes('video', 'create')
 * // Returns: ['text-to-video', 'image-to-video']
 *
 * getValidSubPurposes('sound', 'create')
 * // Returns: ['text-to-speech', 'text-to-music', 'text-to-sound']
 * ```
 */
export function getValidSubPurposes(
  nodeType: AiNodeType,
  purpose: AiModelPurpose
): readonly string[] {
  const nodeMap = NODE_SUB_PURPOSE_MAP[nodeType];
  if (!nodeMap) return [];

  const subPurposes = nodeMap[purpose];
  return subPurposes || [];
}

/**
 * Validate if a subPurpose is valid for the given nodeType + purpose
 *
 * @param nodeType - The node type
 * @param purpose - The purpose
 * @param subPurpose - The subPurpose to validate
 * @returns true if valid, false otherwise
 *
 * @example
 * ```typescript
 * isValidSubPurpose('image', 'enhance', 'upscale') // true
 * isValidSubPurpose('image', 'enhance', 'video-upscale') // false
 * isValidSubPurpose('sound', 'create', 'text-to-speech') // true
 * isValidSubPurpose('3d', 'edit', 'remesh') // true
 * ```
 */
export function isValidSubPurpose(
  nodeType: AiNodeType,
  purpose: AiModelPurpose,
  subPurpose: string
): boolean {
  const validSubPurposes = getValidSubPurposes(nodeType, purpose);
  return validSubPurposes.includes(subPurpose);
}

/**
 * Get all subPurposes for a specific nodeType (across all purposes)
 *
 * @param nodeType - The node type
 * @returns Array of all subPurpose strings for this nodeType
 *
 * @example
 * ```typescript
 * getAllSubPurposesForNodeType('sound')
 * // Returns: ['text-to-speech', 'text-to-music', 'text-to-sound', 'audio-enhance', 'transcription', 'voice-conversion', 'audio-analysis', 'waveform']
 * ```
 */
export function getAllSubPurposesForNodeType(nodeType: AiNodeType): string[] {
  const nodeMap = NODE_SUB_PURPOSE_MAP[nodeType];
  if (!nodeMap) return [];

  const allSubPurposes = new Set<string>();
  Object.values(nodeMap).forEach((subPurposes) => {
    subPurposes.forEach((sp: string) => allSubPurposes.add(sp));
  });

  return Array.from(allSubPurposes);
}

/**
 * Get human-readable label for a subPurpose
 *
 * @param subPurpose - The subPurpose value
 * @returns Human-readable label
 *
 * @example
 * ```typescript
 * getSubPurposeLabel('upscale') // 'Upscale'
 * getSubPurposeLabel('text-to-video') // 'Text To Video'
 * getSubPurposeLabel('background-removal') // 'Background Removal'
 * ```
 */
export function getSubPurposeLabel(subPurpose: string): string {
  return subPurpose
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// =============================================================================
// TOOL CATEGORIES
// =============================================================================

/**
 * Tool Category definitions — higher-level grouping above subPurpose.
 * Used across toolbar, library, and admin for consistent tool organization.
 */
export const TOOL_CATEGORIES = {
  'fill-and-extend': { label: 'Fill & Extend', icon: 'paint-bucket' },
  'remove-and-clean': { label: 'Remove & Clean', icon: 'eraser' },
  'style-and-color': { label: 'Style & Color', icon: 'palette' },
  'face-and-body': { label: 'Face & Body', icon: 'scan-face' },
  'control-and-guidance': { label: 'Control & Guidance', icon: 'sliders-horizontal' },
  'analyze-and-caption': { label: 'Analyze & Caption', icon: 'scan-text' },
  'convert': { label: 'Convert', icon: 'arrow-right-left' },
  'motion': { label: 'Motion', icon: 'clapperboard' },
  'voice': { label: 'Voice', icon: 'mic' },
  'commercial': { label: 'Commercial', icon: 'shopping-bag' },
  'training': { label: 'Training', icon: 'brain' },
} as const;

export const TOOL_CATEGORY_VALUES = [
  'fill-and-extend', 'remove-and-clean', 'style-and-color',
  'face-and-body', 'control-and-guidance', 'analyze-and-caption',
  'convert', 'motion', 'voice', 'commercial', 'training',
] as const;

export const ToolCategorySchema = z.enum(TOOL_CATEGORY_VALUES);
export type ToolCategory = z.infer<typeof ToolCategorySchema>;

/**
 * Canonical mapping from subPurpose → toolCategory.
 * Single source of truth used by sync pipeline, toolbar, library, and admin.
 */
export const SUB_PURPOSE_TO_TOOL_CATEGORY: Record<string, ToolCategory> = {
  // Fill & Extend
  'inpaint': 'fill-and-extend',
  'outpaint': 'fill-and-extend',
  'video-extend': 'fill-and-extend',

  // Remove & Clean
  'background-removal': 'remove-and-clean',
  'object-removal': 'remove-and-clean',

  // Style & Color
  'style-transfer': 'style-and-color',
  'reimagine': 'style-and-color',
  'colorization': 'style-and-color',
  'relighting': 'style-and-color',

  // Face & Body
  'face-swap': 'face-and-body',
  'face-retouching': 'face-and-body',
  'virtual-try-on': 'face-and-body',
  'lipsync': 'face-and-body',

  // Control & Guidance
  'controlnet': 'control-and-guidance',
  'mask': 'control-and-guidance',
  'depth-estimation': 'control-and-guidance',
  'pose-estimation': 'control-and-guidance',
  'edge-detection': 'control-and-guidance',
  'segmentation': 'control-and-guidance',

  // Analyze & Caption
  'captioning': 'analyze-and-caption',
  'video-captioning': 'analyze-and-caption',
  'video-segmentation': 'analyze-and-caption',
  'audio-analysis': 'analyze-and-caption',

  // Convert
  'vectorization': 'convert',
  'frame-extraction': 'convert',
  'ffmpeg': 'convert',
  'waveform': 'convert',

  // Motion
  'frame-interpolation': 'motion',
  'motion-transfer': 'motion',
  'reframe': 'style-and-color',

  // Voice
  'transcription': 'voice',
  'voice-conversion': 'voice',

  // Commercial
  'product-photography': 'commercial',

  // Training
  'training': 'training',
};

/**
 * Get the toolCategory for a given subPurpose.
 */
export function getToolCategory(subPurpose: string): ToolCategory | null {
  return SUB_PURPOSE_TO_TOOL_CATEGORY[subPurpose] ?? null;
}

/**
 * Get tool categories with their subPurposes, filtered by nodeType.
 * Returns only categories that have at least one subPurpose for the given nodeType.
 *
 * @example
 * ```typescript
 * getToolCategoriesForNodeType('image')
 * // Returns: [
 * //   { category: 'fill-and-extend', label: 'Fill & Extend', icon: 'expand', subPurposes: ['inpaint', 'outpaint'] },
 * //   { category: 'remove-and-clean', label: 'Remove & Clean', icon: 'eraser', subPurposes: ['background-removal', 'object-removal'] },
 * //   ...
 * // ]
 * ```
 */
export function getToolCategoriesForNodeType(nodeType: AiNodeType): Array<{
  category: ToolCategory;
  label: string;
  icon: string;
  subPurposes: string[];
}> {
  const validSubPurposes = getValidSubPurposes(nodeType, 'tools');

  // Group valid subPurposes by their toolCategory
  const grouped: Record<string, string[]> = {};
  for (const sp of validSubPurposes) {
    const cat = SUB_PURPOSE_TO_TOOL_CATEGORY[sp];
    if (cat) {
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(sp);
    }
  }

  // Return in TOOL_CATEGORIES order, skipping empty categories
  return TOOL_CATEGORY_VALUES
    .filter((cat) => grouped[cat]?.length)
    .map((cat) => ({
      category: cat,
      label: TOOL_CATEGORIES[cat].label,
      icon: TOOL_CATEGORIES[cat].icon,
      subPurposes: grouped[cat],
    }));
}
